import React from 'react';
import { AuthAPI } from '../api/auth';
import { clearAuthSessionStorage, getAuthSession, setAuthSession } from '../utils/authSession';

const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [session, setSessionState] = React.useState(() => getAuthSession());
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const bootstrap = async () => {
      const token = session.token;
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await AuthAPI.bootstrap();
        const data = res?.data || {};
        const next = setAuthSession({
          token,
          user: data.user,
          enterprise: data.enterprise,
          role: data.role,
          memberships: data.memberships || [],
          apps: data.apps || [],
        });
        setSessionState(next);
      } catch {
        clearAuthSessionStorage();
        setSessionState({ token: null, user: null, enterprise: null, role: null, memberships: [], apps: [], selected_app: null });
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  React.useEffect(() => {
    const handleStorage = (event) => {
      const trackedKeys = new Set([
        'susha_auth_token',
        'susha_auth_user',
        'susha_auth_enterprise',
        'susha_auth_role',
        'susha_auth_memberships',
        'susha_auth_apps',
        'susha_auth_selected_app',
      ]);

      if (event.storageArea !== localStorage || !trackedKeys.has(event.key)) return;

      const next = getAuthSession();
      setSessionState(next);
      if (!next.token || !next.user) {
        setLoading(false);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setSession = React.useCallback((payload) => {
    const next = setAuthSession(payload);
    setSessionState(next);
    return next;
  }, []);

  const login = React.useCallback(async (email, password) => {
    const res = await AuthAPI.login(email, password);
    const data = res?.data || {};
    setSession(data);
    return data;
  }, [setSession]);

  const register = React.useCallback(async (payload) => {
    const res = await AuthAPI.register(payload);
    return res?.data || {};
  }, []);

  const logout = React.useCallback(async () => {
    const token = session.token;
    clearAuthSessionStorage();
    setSessionState({ token: null, user: null, enterprise: null, role: null, memberships: [], apps: [], selected_app: null });

    if (token) {
      void AuthAPI.logout().catch(() => {
        // logout is best-effort; the local session is already cleared
      });
    }
  }, [session.token]);

  const value = React.useMemo(() => ({
    ...session,
    loading,
    isAuthenticated: Boolean(session.token && session.user),
    setSession,
    login,
    register,
    logout,
  }), [session, loading, setSession, login, register, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
